/**
 * ActiveFilterChips — what's currently narrowing the ledger, and how to undo it.
 *
 * v3.19.4. Filters lived only inside their dropdowns, so a filter left on from
 * an earlier drill-down silently hid rows and read as "my transactions are
 * missing". Each active filter now gets a removable chip. The date range is
 * deliberately absent — it already has its own always-visible preset row.
 *
 * Renders nothing when no filter is active.
 */
import React from 'react';
import { X } from 'lucide-react';

export function ActiveFilterChips({ filters = [], onClearAll, resultCount }) {
  const active = filters.filter(Boolean);
  if (active.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="transactions-active-filters">
      <span className="text-[11px] uppercase tracking-widest text-muted-foreground/70">Filtered by</span>
      {active.map((f) => (
        <button
          key={f.key}
          type="button"
          onClick={f.onRemove}
          title={`Remove ${f.label} filter`}
          className="group inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-1 text-xs
                     transition-colors hover:border-destructive/40 hover:bg-destructive/5
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`transactions-filter-chip-${f.key}`}
        >
          <span className="text-muted-foreground">{f.label}</span>
          <span className="font-medium max-w-[180px] truncate">{f.value}</span>
          <X className="h-3 w-3 text-muted-foreground/70 transition-colors group-hover:text-destructive" />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
        data-testid="transactions-filter-chips-clear-all"
      >
        Clear all
      </button>
      {typeof resultCount === 'number' && (
        <span className="text-[11px] text-muted-foreground" data-testid="transactions-filter-chips-count">
          {resultCount} {resultCount === 1 ? 'match' : 'matches'}
        </span>
      )}
    </div>
  );
}

export default ActiveFilterChips;
